import moment from "moment";

import { Person } from "@/Common/Models/Person/Person";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { CommunicationsHelper } from "@/Communications/Helpers/CommunicationsHelper";
import { GlobalSendCommunicationStore } from "@/Communications/Stores/GlobalSendCommunicationStore";

class SendRecallPopupHandler extends BaseOperationHandler {
    constructor() {
        super();

        this.RegisterOperation(LocalOperations.SendRecallPopup.Show_SendRecall_Popup, this.ShowPopup);
        this.RegisterOperation(LocalOperations.SendRecallPopup.ClosePopup, this.ClosePopup);
        this.RegisterOperation(LocalOperations.SendRecallPopup.ClearClick, this.ClearPopup);
        this.RegisterOperation(LocalOperations.SendRecallPopup.PatientSelect, this.SelectPatient);
        this.RegisterOperation(LocalOperations.SendRecallPopup.DescriptionSelect, this.DescriptionSelect);
        this.RegisterOperation(LocalOperations.SendRecallPopup.DescriptionChange, this.DescriptionChange);
        this.RegisterOperation(LocalOperations.SendRecallPopup.ClinicianSelect, this.ClinicianSelect);
        this.RegisterOperation(LocalOperations.SendRecallPopup.SpecialityChange, this.SpecialityChange);
        this.RegisterOperation(LocalOperations.SendRecallPopup.DueDateSelect, this.DueDateSelect);
        this.RegisterOperation(LocalOperations.SendRecallPopup.DueDateToday, this.DueDateToday);
        this.RegisterOperation(LocalOperations.SendRecallPopup.PriorityChange, this.PriorityChange);
        this.RegisterOperation(LocalOperations.SendRecallPopup.NoteChange, this.NoteChange);
        this.RegisterOperation(LocalOperations.SendRecallPopup.CreateClick, this.CreateClick);
    }

    private ShowPopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const patient = request.Get<Person>("patient");
        const isSentFromEncounter = request.Get<boolean>("isSentFromEncounter") || false;
        const encounterId = request.Get<string>("encounterId");

        store.SendRecallPopupModel = Immutable.fromJS(CommunicationsHelper.GetDefaultRecallPopupModel());

        if (patient) {
            store.SendRecallPopupModel = store.SendRecallPopupModel
                .set("SelectedPatients", Immutable.List([patient]))
                .set("SelectedClinicians", Immutable.List(sessionStore.prefillRecallSelectedClinicians ?? [cacheStore.GetCliniciansPersonsByContactId(sessionStore.getCurrentEmployee().ContactId)]));
        }

        store.SendRecallPopupModel = store.SendRecallPopupModel
            .set("IsSentFromEncounter", isSentFromEncounter)
            .set("IsPopupVisible", true)
            .set("EncounterId", encounterId);
    }

    private ClosePopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendRecallPopupModel = Immutable
            .fromJS(CommunicationsHelper.GetDefaultRecallPopupModel())
            .set("IsPopupVisible", false);
    }

    private ClearPopup(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.SendRecallPopupModel = Immutable
            .fromJS(CommunicationsHelper.GetDefaultRecallPopupModel())
            .set("IsPopupVisible", true);
    }

    private SelectPatient(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const patientItem = request.Get<PersonDropdownItem>("value");
        const selectedPatients = store.SendRecallPopupModel.get("SelectedPatients").toArray();

        // add patient or remove if already present
        function indexOf(id: string, collection: Person[]): number {
            var idx = -1;
            for (var i = 0; i < collection.length; i++) {
                if (id === collection[i].ContactId) {
                    idx = i;
                    break;
                }
            }
            return idx;
        }
        const idx = indexOf(patientItem.Item.ContactId, selectedPatients);
        if (idx === -1)
            selectedPatients.push(patientItem.Item);
        else
            selectedPatients.splice(idx, 1);

        store.SendRecallPopupModel = store.SendRecallPopupModel.set("SelectedPatients", Immutable.List(selectedPatients));
    }

    private DescriptionChange(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const value = request.Get<string>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("DescriptionText", value);
    }

    private DescriptionSelect(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const val = request.Get<KeyValuePair<string, string>>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("DescriptionText", val.Value);
    }

    private ClinicianSelect(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const selectedClinician = request.Get<PersonDropdownItem>("value");
        if (selectedClinician == null || selectedClinician.Item == null)
            return;

        let selectedClinicians = store.SendRecallPopupModel.get("SelectedClinicians").toArray();

        if (selectedClinicians.indexOf(selectedClinician.Item) > -1)
            selectedClinicians = selectedClinicians.filter(x => x.ContactId != selectedClinician.Item.ContactId);
        else
            selectedClinicians.push(selectedClinician.Item);

        store.SendRecallPopupModel = store.SendRecallPopupModel.set("SelectedClinicians", Immutable.List(selectedClinicians));
    }

    private SpecialityChange(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const specialityId = request.Get<string>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("SelectedSpecializationId", specialityId);
    }

    private DueDateSelect(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const dueDate = request.Get<Date>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("DueDate", dueDate);
    }

    private DueDateToday(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const today = moment().toDate();
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("DueDate", today);
    }

    private PriorityChange(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const priorityId = request.Get<string>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("SelectedPriorityId", priorityId);
    }

    private NoteChange(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        const newNote = request.Get<string>("value");
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("Notes", newNote);
    }

    private CreateClick(request: LocalEvent, store: GlobalSendCommunicationStore): void {
        store.RecallActions.CreateRecall(
            store,
            store.SendRecallPopupModel.get("IsSentFromEncounter"),
            store.SendRecallPopupModel.get("EncounterId")
        );

        if (cacheStore.ClinicSettings.EnableAutofillRecipientInCommunicationTab) {
            const selectedClinicians = store.SendRecallPopupModel.get("SelectedClinicians").toArray();
            sessionStore.prefillRecallSelectedClinicians = selectedClinicians;
        }
        store.SendRecallPopupModel = store.SendRecallPopupModel.set("IsPopupVisible", false);
    }
}

export { SendRecallPopupHandler };
